import React, { ForwardRefRenderFunction, useState, useImperativeHandle } from "react";

import { useTranslation } from "react-i18next";
import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import * as yup from "yup";

import { CustomerDetails } from "Models/shipments/shipments.tracking";
import { Customer } from "Models/customer";

import Modal from "Components/Generic/Modal";
import TextField from "Components/Form/Input";
import AutoSuggest from "Components/Form/AutoSuggest";
import PhoneInput from "Components/Form/PhoneInput";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";

import getOptionLabelCustomer from "Util/getOptionLabelCustomer";
import { CustomerKeys } from "Util/queries";
import { Status } from "Util/enums";
import CustomerService from "Services/customer.service";
import { useAppState } from "Context/StateContext";
import useYupValidation from "Hooks/useYupValidation";

import useStyles, { ModalContent } from "./styles";

interface QuickOrderProps {
  onAddQuickOrder: (quickOrder: CustomerDetails) => void;
  customersToShipments: CustomerDetails[];
}

export interface QuickOrderHandler {
  open: () => void;
  close: () => void;
}

export const QuickOrderModal: ForwardRefRenderFunction<QuickOrderHandler, QuickOrderProps> = (
  { onAddQuickOrder, customersToShipments },
  ref
) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetails>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError } = useYupValidation(errors || []);

  const { data: customerList } = useQuery({
    queryKey: [CustomerKeys.all],
    queryFn: () =>
      CustomerService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }).then((response) => response.data || []),
  });

  const onClose = () => {
    setCustomerDetails(undefined);
    setOpen(false);
  };

  const onAddNewQuickOrder = () => {
    if (customerDetails && onValidate(customerDetails)) {
      const alreadyExists = customersToShipments?.find(
        (customer) => customer.order === customerDetails.order
      );

      if (alreadyExists) {
        NotificationToast.error(translate("orderAlreadyExists"));
        return;
      }

      const quickOrderAlreadyExists = customersToShipments?.some((cts) =>
        cts.customer?.id && customerDetails.customer?.id
          ? cts.customer?.id === customerDetails.customer?.id
          : false
      );

      if (quickOrderAlreadyExists) {
        NotificationToast.error(translate("aCustomerWithSameIdAlreadyExists"));
        return;
      }

      onAddQuickOrder(customerDetails);
      onClose();
    }
  };

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: onClose,
  }));

  const getPhoneValue = (value: string) => {
    if (!value || !value.trim().length) return "";

    return value.includes("+") ? value : `+${value}`;
  };

  const schema = yup.object().shape({
    order: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    customer: yup.object().shape({
      name: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      email: yup
        .string()
        .email(translate("emailIsInvalid"))
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      phone: yup.string().nullable(),
    }),
  });

  const onValidate = (quickOrder: CustomerDetails) => {
    try {
      schema.validateSync(quickOrder, { abortEarly: false });
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
      }

      return false;
    }
  };

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={translate("createQuickOrder")}
      className={classes.modal}
      showActionButtons
      actionButtonProps={{
        textSave: translate("create"),
        textCancel: translate("cancel"),
        onCancel: onClose,
        onSave: onAddNewQuickOrder,
        saveDisabled:
          !customerDetails?.order ||
          !customerDetails?.customer?.email ||
          !customerDetails?.customer?.name,
      }}
    >
      <ModalContent.Body>
        <ModalContent.Title>
          <span>{translate("customerDetails")}</span>
        </ModalContent.Title>

        <Grid container spacing={4}>
          <Grid item xs={4}>
            <TextField
              required
              label={`${translate("order")} #`}
              placeholder="123456789"
              value={customerDetails?.order || ""}
              onChange={(e) =>
                setCustomerDetails((prev) => ({ ...(prev || {}), order: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={4}>
            <AutoSuggest
              required
              noOptionsText={translate("searchOrAddCustomer")}
              label={translate("firstName")}
              inputValue={customerDetails?.customer?.name || ""}
              getOptionLabel={(customer: Customer) => getOptionLabelCustomer(customer)}
              getOptionSelected={(customer: Customer, value: Customer) => customer.id === value.id}
              options={customerList || []}
              onChange={(customer: Customer) =>
                setCustomerDetails((prev) => ({ ...(prev || {}), customer }))
              }
              onChangeInput={(value: string) => {
                setCustomerDetails((prev) => ({
                  ...(prev || {}),
                  customer: {
                    ...(prev?.customer || {}),
                    name: value,
                    fullName: `${value} ${customerDetails?.customer?.lastName || ""}`,
                  },
                }));
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              required
              label={translate("lastName")}
              placeholder="Doe"
              disabled={!!customerDetails?.customer?.id}
              value={customerDetails?.customer?.lastName || ""}
              onChange={(e) =>
                setCustomerDetails((prev) => ({
                  ...(prev || {}),
                  customer: {
                    ...(prev?.customer || {}),
                    lastName: e.target.value,
                    fullName: `${prev?.customer?.name || ""} ${e.target.value}`,
                  },
                }))
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              label={translate("email")}
              placeholder="john.doe@example.com"
              disabled={!!customerDetails?.customer?.id}
              value={customerDetails?.customer?.email || ""}
              onChange={(e) =>
                setCustomerDetails((prev) => ({
                  ...(prev || {}),
                  customer: { ...(prev?.customer || {}), email: e.target.value },
                }))
              }
            />
          </Grid>
          <Grid item xs={6}>
            <PhoneInput
              label={translate("phone")}
              defaultCountry="AU"
              international
              disabled={!!customerDetails?.customer?.id}
              value={customerDetails?.customer?.phone || ""}
              onChange={(value) =>
                setCustomerDetails((prev) => ({
                  ...(prev || {}),
                  customer: { ...(prev?.customer || {}), phone: getPhoneValue(value) },
                }))
              }
            />
          </Grid>
        </Grid>
      </ModalContent.Body>
    </Modal>
  );
};

const QuickOrderModalComponent = React.forwardRef(QuickOrderModal);

export { QuickOrderModalComponent };
